import { SERVICE_BASE_URLS } from "../global/constants";
import {
  IExposure,
  IPortCapacity,
  IPreflight,
  IReconcilePlan,
  IReconcileResult,
} from "../types/api/istio.type";
import api from "./api";

const ROOT = `${SERVICE_BASE_URLS.DB_MANAGER}/istio/db-gateway`;

const getPortCapacity = () => api.get<IPortCapacity>(`${ROOT}/ports`);

/**
 * Can an environment be exposed under this name right now?
 *
 * envId is omitted on the create path, where the environment does not exist yet; the
 * already-exposed check is then skipped and the answer is about capacity and the name.
 */
const preflight = (envId?: number, name?: string) => {
  const params = new URLSearchParams();
  if (envId != null) params.set("env_id", String(envId));
  if (name) params.set("name", name);
  const query = params.toString();
  return api.get<IPreflight>(`${ROOT}/preflight${query ? `?${query}` : ""}`);
};

const getExposure = (envId: number) => api.get<IExposure>(`${ROOT}/exposures/${envId}`);

const createExposure = (envId: number, name: string | undefined, tenantId: number) =>
  api.post<IExposure>(
    `${ROOT}/exposures`,
    { env_id: envId, name: name || undefined },
    { headers: { "tenant-id": String(tenantId) } }
  );

/** Disconnects every client on that port, so the server logs who asked for it. */
const deleteExposure = (envId: number, tenantId: number, userId?: string) =>
  api.delete(`${ROOT}/exposures/${envId}`, {
    headers: { "tenant-id": String(tenantId), ...(userId ? { "user-id": userId } : {}) },
  });

/** Read-only: what the durable record and the cluster disagree about. */
const reconcilePlan = () => api.get<IReconcilePlan>(`${ROOT}/reconcile/plan`);

/**
 * Rebuild the `missing` bucket on its recorded port and name.
 *
 * Omitting `envIds` applies every one of them — the DR path. Passing a single id is the
 * per-row repair. `userId` is required: these act across every tenant, so the server records
 * the caller instead of checking ownership.
 */
const reconcileApply = (userId: string, envIds?: number[]) =>
  api.post<IReconcileResult>(`${ROOT}/reconcile`, envIds?.length ? { env_ids: envIds } : {}, {
    headers: { "user-id": userId },
  });

/** Record what is live, for unrecorded exposures and stale records. Touches no K8s object. */
const reconcileAdopt = (userId: string, envIds?: number[]) =>
  api.post<IReconcileResult>(`${ROOT}/reconcile/adopt`, envIds?.length ? { env_ids: envIds } : {}, {
    headers: { "user-id": userId },
  });

/**
 * Pulls the structured `detail` the API returns.
 *
 * `api.ts` has a response interceptor that rejects with `error.response.data` rather than the
 * AxiosError, so what arrives here is usually the API body already — both shapes are handled.
 */
export const exposureErrorDetail = (error: any): { code?: string; message?: string; [k: string]: any } => {
  // No response at all: the request never completed, so there is no API error to report.
  // Naming it matters — a blocked CORS preflight and a dead backend look identical otherwise.
  if (error?.response === undefined && (error?.code || error?.isAxiosError)) {
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    return {
      code: error.code || "network_error",
      message: offline
        ? "You appear to be offline — the request was never sent."
        : error.code === "ECONNABORTED"
        ? "The API did not respond in time."
        : "Could not reach the API. The request was blocked or the service is unreachable " +
          "(a browser console entry will say which).",
    };
  }

  const body = error?.response?.data ?? error;
  const detail = body?.detail ?? body;

  if (Array.isArray(detail)) {
    // FastAPI request validation: name the field, or "Field required" alone says nothing.
    const message = detail
      .map((d: any) => {
        const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : undefined;
        return field && d?.msg ? `${field}: ${d.msg}` : d?.msg;
      })
      .filter(Boolean)
      .join("; ");
    return { code: "validation_error", message: message || "Request validation failed" };
  }
  if (detail && typeof detail === "object") return detail;
  if (typeof detail === "string") return { message: detail };
  return {
    message: error?.message || "The request failed and the server returned no explanation.",
  };
};

const IstioService = {
  getPortCapacity,
  preflight,
  getExposure,
  createExposure,
  deleteExposure,
  reconcilePlan,
  reconcileApply,
  reconcileAdopt,
};

export default IstioService;
